<script lang="ts" setup generic="TObj extends BaseObj">
import type { BaseObj } from '@/objects/BaseObj';
import type { GuiDef } from '@/objects/gui/GuiDef';
import type { FieldDef } from '@/objects/gui/FieldDef';
import type { FieldAlertMessage } from '@/objects';
import DetailEditor from './DetailEditor.vue';
import { InputBool, DisplaySeparator, AccessHide, TabComp } from '@/components';
import { FormLayout, FormLayoutInputSeparator, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { $tm } from '@/locize';
import { ref, computed, type ComponentInstance } from 'vue';
import { useTaskLoader } from '@/composeables';
import DisplayInfotext from '../Basic/DisplayInfotext.vue';
import TooltipHelp from '../Tooltip/TooltipHelp.vue';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import type { CollapsableDef } from '@/objects/gui/CollapsableDef';
import DetailCompCollapsable from './DetailCompCollapsable.vue';
import DisplayEmpty from '../Basic/DisplayEmpty.vue';

const props = defineProps<{
    modelValue: TObj;
    gui: GuiDef<TObj>;
    numPerRow?: number;

    /**
     * Custom GUI group for the object.
     * @param groups The groups of fields to customize.
     */
    customGuiGroups?: (groups: FieldDef<TObj>[][]) => FieldDef<TObj>[][];

    additionalTabs?: TabDefinition[]; // Additional tabs to show in the detail view
}>();

// Keep the original value to compare with
const originalValue: TObj = JSON.parse(JSON.stringify(props.modelValue));

// Reference to the form layout
const formLayout = ref<ComponentInstance<typeof FormLayout>>();

const emit = defineEmits<{
    (e: 'changed', value: boolean): void;
    (e: 'update:modelValue', value: TObj, originalValue: TObj): void;
    (e: 'fullscreen', value: boolean): void;
}>();

/**
 * Set up the modelvalue emits etc
 */
const value = computed<TObj>({
    get: () => props.modelValue,
    set: (value) => {
        emit('update:modelValue', value, originalValue);
        emit('changed', JSON.stringify(value) !== JSON.stringify(originalValue));
    },
});

/**
 * Return the text for the label translated
 * @param field
 * @param smallScreen
 */
const getLabelText = (field: FieldDef<TObj>, smallScreen: boolean): string => {
    if (field.Group && !field.Group.AlwaysShowLabel && !smallScreen) {
        return $tm(field.Group.Group!);
    }

    if (!field.TranslationKey) {
        return field.Name!;
    }

    return $tm(field.TranslationKey!);
};

/**
 * Get the separator for the group
 * @param field
 */
const getLabelSeparator = (field: FieldDef<TObj>) => (!field.Group || !field.Group.Separator ? '' : field.Group.Separator);

/**
 * The detail editors that are being edited
 */
const detailEditors = ref<{ message: FieldAlertMessage }[]>([]);

const isFillContainer = ref(false);
const fillComponentId = ref<string | null>(null);

const onFillContainer = (value: boolean, id?: string) => {
    isFillContainer.value = value;
    fillComponentId.value = value ? (id ?? null) : null;

    if (!isFillContainer.value) {
        formLayout.value?.resizeLabels();
    }
};

/**
 * Get the messages from the detail editors and expose them
 */
defineExpose({
    messages: computed(() =>
        detailEditors.value.filter((editor) => editor != null && editor.message != null && editor.message.alertMessage != null),
    ),
});

// We need to create all the unique tabdefinitions from the guidef
const tabs = new TabContainer();
let fieldTabs = props.gui.Fields.filter((x) => x.Tab != null).map((field) => field.Tab) ?? null;

// Delete duplicates
fieldTabs = fieldTabs.filter((tab, index) => fieldTabs.findIndex((t) => t?.Name == tab?.Name) == index);
fieldTabs.filter((tab) => tab != null).forEach((tab) => tabs.AddTab(new TabDefinition(tab!.Name, tab!.TranslationKey, tab!.Icon)));

// Add the additional tabs if they are provided
if (props.additionalTabs && props.additionalTabs.length > 0) {
    props.additionalTabs.forEach((tab) => tabs.AddTab(tab));
}

// We need to add a default tab if there are no tabs. This will never be shown but is needed for the card component
// TODO: Maybe move this one to the TabComp component. The TabComp should also be refactored to a TabComp
if (tabs.Tabs.length == 0) {
    tabs.AddTab(new TabDefinition('default', 'general.general'));
}

// Replace the computed with a ref. This mean that it wil only be set once and never updated. If we need to update it from outside we should use a watch.
// Or else the callback will be triggered continously and we will end up in an infinite loop.
const guiGroups = ref<FieldDef<TObj>[][]>(
    props.customGuiGroups ? props.customGuiGroups(props.gui.Groups('Detail')) : props.gui.Groups('Detail'),
);

// Group the fields by their collapsable definition
const collapsableGroups = ref<{ key: CollapsableDef | null; fields: FieldDef<TObj>[][] }[]>(
    guiGroups.value.reduce((acc, group) => {
        const collapsableDef = group[0].Collapsable;
        let existingGroup = acc.length > 0 ? acc[acc.length - 1] : null;
        if (existingGroup && existingGroup.key?.Collapsable !== collapsableDef?.Collapsable) {
            existingGroup = null;
        }
        if (existingGroup) {
            existingGroup.fields.push(group);
        } else {
            acc.push({ key: collapsableDef, fields: [group] });
        }
        return acc;
    }, Array<{ key: CollapsableDef | null; fields: FieldDef<TObj>[][] }>()),
);

console.log('Collapsable Groups:', collapsableGroups.value);

/**
 * Set up the loader
 */
const { AddLoader } = useTaskLoader();

/**
 * Load dependencies
 */
AddLoader(props.gui.LoadDependencies, 'general.loadingdependencies');
</script>

<template>
    <TabComp
        :tabs="tabs"
        :fill-content="true"
        :hide-tabs-on-only-one="true"
        :pinable="false"
    >
        <template
            v-for="(tab, i) in tabs.Tabs"
            :key="i"
            v-slot:[`tab-${tab?.Name}`]
        >
            <slot
                :name="`tab-${tab?.Name}`"
                v-if="`tab-${tab?.Name}` in $slots"
            ></slot>
            <FormLayout
                v-else
                ref="formLayout"
            >
                <DetailCompCollapsable
                    v-for="(collapsable, i) in collapsableGroups"
                    :key="(collapsable.key?.Collapsable ?? 'NULL') + i.toString()"
                    :collapsable="collapsable.key != null"
                    :openByDefault="false"
                    :heading="collapsable.key?.TranslationKey ?? ''"
                >
                    <FormLayoutRow
                        v-for="(components, i) in collapsable.fields"
                        :key="i"
                    >
                        <template
                            v-for="(field, ii) in components"
                            :key="ii"
                        >
                            <template v-if="field.Tab === null || field.Tab?.Name == tab?.Name">
                                <div
                                    class="d-md-flex flex-fill align-items-sm-center"
                                    :class="{
                                        'w-md-50': components.length == 2,
                                        'w-md-33': components.length == 3,
                                        'w-md-25': components.length == 4,
                                    }"
                                >
                                    <AccessHide
                                        :className="gui.Name"
                                        :field="field.Name!"
                                    >
                                        <!-- This is the label in the first column (either for an DetailEditor or for a group) of the row and should be visible always unless the component is a row separator. 
                 If the component is a InputBool(CheckBox) we dont show the text, but we render the label so it is alligned correctly.
                 This one is only showed on larger devies -->
                                        <FormLayoutLabel
                                            v-if="
                                                components[0].Group &&
                                                components[0].Group.AlwaysShowLabel &&
                                                field.DetailEditor != DisplaySeparator &&
                                                field.DetailEditor != DisplayInfotext &&
                                                field.DetailEditor != DisplayEmpty
                                            "
                                            :text="field.DetailEditor == InputBool ? '' : getLabelText(field, false)"
                                            :largeonly="true"
                                            :tooltip="field.Tooltip ?? undefined"
                                        />

                                        <FormLayoutLabel
                                            v-else-if="
                                                !isFillContainer &&
                                                field.DetailEditor != DisplaySeparator &&
                                                field.DetailEditor != DisplayInfotext &&
                                                field.DetailEditor != DisplayEmpty &&
                                                (!components[0].Group || (components[0].Group && ii == 0))
                                            "
                                            :text="field.DetailEditor == InputBool ? '' : getLabelText(field, false)"
                                            :largeonly="true"
                                            :tooltip="field.Tooltip ?? undefined"
                                        />

                                        <!-- This is the actual label of the DetailEditor and will be rendered both in the first column and infront of each element.
                  This is only visible on small devices. -->
                                        <FormLayoutLabel
                                            v-if="
                                                !isFillContainer &&
                                                field.DetailEditor != DisplaySeparator &&
                                                field.DetailEditor != DisplayInfotext &&
                                                field.DetailEditor != DisplayEmpty
                                            "
                                            :text="getLabelText(field, true)"
                                            :smallonly="true"
                                            :tooltip="field.Tooltip ?? undefined"
                                        />

                                        <!-- This is the separator value between the different DetailEditor in a group -->
                                        <FormLayoutInputSeparator
                                            v-if="
                                                !components[0].Group?.AlwaysShowLabel && !isFillContainer && components[0].Group && ii > 0
                                            "
                                            :separator="getLabelSeparator(field)"
                                        />

                                        <!-- This is the actual DetailEditor that is being edited -->

                                        <DetailEditor
                                            v-if="!isFillContainer || fillComponentId == `${i}_${ii}`"
                                            v-model="value"
                                            :field="field"
                                            :id="`${i}_${ii}`"
                                            :label="field.DetailEditor == InputBool ? getLabelText(field, false) : null"
                                            @fillcontainer="onFillContainer"
                                            :class="[
                                                field.CssClasses,
                                                { 'is-fullscreen': isFillContainer && fillComponentId == `${i}_${ii}` },
                                            ]"
                                            ref="detailEditors"
                                            :readonly="!field.Editable"
                                        ></DetailEditor>

                                        <!-- We need to add tooltip at the end if it is a inputbool -->
                                        <TooltipHelp
                                            v-if="field.DetailEditor == InputBool && field.Tooltip"
                                            :text="field.Tooltip"
                                        />
                                    </AccessHide>
                                </div>
                            </template>
                        </template>
                    </FormLayoutRow>
                </DetailCompCollapsable>
            </FormLayout>
        </template>
    </TabComp>
</template>
